import { ChangeEventHandler, ComponentProps, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Table, Button } from '@robocorp/components';

import { WorkItemFile } from './FileRow';
import { FileList } from '~/components';
import { useDisclosure } from '~/hooks';

const SHOWN_FILE_LIMIT = 5;

interface Data {
  id?: string;
  index: number;
  name: string;
  size: number;
}

interface Props extends Pick<ComponentProps<typeof Table>, 'rowCount'> {
  workItemId: string;
  data: Data[];
  onRemove?: (index: number) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

export const WorkItemFileList = forwardRef<
  HTMLInputElement,
  Omit<Props, 'rowCount'> & { rowCount?: number; newFileCount?: number }
>(
  (
    { workItemId, data, onChange, onRemove, rowCount = SHOWN_FILE_LIMIT, newFileCount = 0 },
    fileInputRef,
  ) => {
    const [showAllArtifacts, allArtifactsHandler] = useDisclosure(false);
    const shownArtifacts = showAllArtifacts
      ? data
      : data.slice(0, Math.max(rowCount, newFileCount));

    const { t } = useTranslation();

    return (
      <>
        {data.length > 0 ? (
          <Box pt="$24">
            <FileList>
              {shownArtifacts.map((file, index) => (
                <WorkItemFile
                  key={file.id}
                  {...file}
                  workItemId={workItemId}
                  remove={onRemove}
                  index={index}
                />
              ))}
            </FileList>
            {data.length > rowCount && !showAllArtifacts && (
              <Box display="flex" pt="$16" onClick={allArtifactsHandler.show}>
                <Button flex={1}>{t('stepRun.showAllFiles')}</Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height={90}>
            <Typography color="grey30">{t('noWorkItemFiles')}</Typography>
          </Box>
        )}

        <input
          ref={fileInputRef}
          id="work-item-file-input"
          type="file"
          onChange={onChange}
          style={{ display: 'none' }}
          multiple
        />
      </>
    );
  },
);
